
import { dataUrlToFile } from "./convertUtils";
import { uploadToStorage } from "./uploadUtils";

/**
 * Utility class for handling image operations.
 */
export class ImageUploader {
  /**
   * Downloads an image from a URL and uploads it to storage.
   * @param url The URL of the image to download
   * @param folder The folder to upload the image to
   * @returns The public URL of the uploaded image
   */
  static async downloadAndUpload(url: string, folder: string = "blog_images/inline"): Promise<string> {
    try {
      // For Bubble.io URLs, use our proxy with base64 encoding
      if (url.includes('bubble.io')) {
        return await this.downloadWithProxyAndUpload(url, folder);
      }
      
      // For other URLs, fetch directly
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.statusText}`);
      }
      
      // Get the image blob
      const blob = await response.blob();
      
      // Create a File object from the blob
      const filename = url.split('/').pop() || `image_${Date.now()}`;
      const file = new File([blob], filename, { type: blob.type });
      
      return await uploadToStorage(file, folder);
    } catch (error: any) {
      console.error("Error downloading and uploading image:", error);
      throw error;
    }
  }
  
  /**
   * Downloads an image using the proxy with base64 encoding and uploads it to storage.
   * @param url The URL of the Bubble.io image
   * @param folder The folder to upload to
   * @returns The public URL of the uploaded image
   */
  static async downloadWithProxyAndUpload(url: string, folder: string): Promise<string> {
    console.log(`Using proxy with base64 encoding for Bubble.io URL: ${url}`);
    
    try {
      // Call our edge function to get the image as base64
      const response = await fetch('/api/image-proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url, base64: true }),
      });

      if (!response.ok) {
        throw new Error(`Edge function error: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data || !data.dataUrl) {
        throw new Error('No data URL returned from proxy');
      }
      
      // Convert the data URL to a file
      const extension = data.contentType.split('/')[1] || 'webp';
      const filename = `bubble_image_${Date.now()}.${extension}`;
      const file = dataUrlToFile(data.dataUrl, filename);
      
      console.log(`Successfully converted proxy data to file: ${file.size} bytes`);
      
      return await uploadToStorage(file, folder);
    } catch (error: any) {
      console.error("Proxy download error:", error);
      throw error;
    }
  }
  
  /**
   * Uploads an image to storage.
   * @param file The file to upload
   * @param folder The folder to upload to
   * @returns The public URL of the uploaded image
   */
  static async uploadToStorage(file: File, folder: string = "blog_images/inline"): Promise<string> {
    return uploadToStorage(file, folder);
  }
}
